import { Flex, VStack } from '@chakra-ui/react'
import { findAll } from 'highlight-words-core'
import { t } from 'i18next'
import { concat, equals, propEq, filter as ramdaFilter } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'
import styled from 'styled-components'
import markAsRead from '~/assets/svgs/icon-mark-as-read.svg?react'
import { useModalState } from '~/atoms/ModalState'
import {
  DotNotifier,
  FilterWrapper,
  ItemWrapper,
  ListWrapper,
  NotificationWrapper,
  StyledItemWrapper,
  UnreadWrapper,
} from '~/components/compositional/ListViewStyles'
import { FilterMenu, Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import EmptySearch from '~/components/EmptySearch'
import { EmptyState } from '~/components/EmptyState'
import { ToggleListButton } from '~/components/layout/ResponsivePage'
import ListItem from '~/components/ListItem'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import { ChatFilters, ChatOptions } from '~/constants/config'
import { light } from '~/constants/theme'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  MessagingEmitterEventType,
  ThreadCreatedPayload,
} from '~/services/EventEmitter/types'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { formatDateString } from '@mangovoice/core'
import { MessageGroup, MessageThread } from '@mangovoice/types'

import {
  NameWrapper,
  StyledSubtitleText,
  StyledTitleText,
  TitleWrapper,
} from './Messaging.styles'

const FILTER_CHANGE_DEBOUNCE_MS = 200
const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`

interface MessageThreadsListViewProps {
  onMessageThreadSelected: (
    thread: MessageThread,
    messageGroup?: MessageGroup,
  ) => void
}
const MessageThreadsListView = ({
  onMessageThreadSelected,
}: MessageThreadsListViewProps) => {
  const [visibleMessageThreads, setVisibleMessageThreads] =
    useState<MessageThread[]>()
  const [showLoader, setShowLoader] = useState(true)
  const [firstLoadStarted, setFirstLoadStarted] = useState(false)
  const [unreadsFilter, setUnreadsFilter] = useState<number>()
  const [searchValue, setSearchValue] = useState(SearchValue.empty)
  const { userMessageGroups } = useMessageGroupsViewModel()
  const {
    messagingModal: [messagingModalState, setMessagingModalState],
  } = useModalState()
  const [pendingAutoSelectedThreadId, setPendingAutoSelectedThreadId] =
    useState<number>()

  const [selectedGroup, setSelectedGroup] = useState(0)

  const { subscribe: emitterSubscribe } = useEmitterService()

  const {
    messageThreadsLoading,
    selectedThread,
    messageThreads,
    setFilters,
    getMessageThreads,
    tryGetNextThreadPage,
    markMessageThreadRead,
    setSelectedThread,
  } = useMessageThreadsViewModel()

  const {
    messagingMarkAllReadModal: [, setChatMarkAllReadModalState],
  } = useModalState()

  const unreadThreads = visibleMessageThreads?.filter(
    (thread) => thread.unread_message_count > 0,
  )
  const data = equals(unreadsFilter ?? 0, ChatFilters.RECENT)
    ? visibleMessageThreads
    : unreadThreads

  const threads =
    selectedGroup === 0
      ? data
      : data?.filter((thread) => thread.message_group_id === selectedGroup)

  const groups = userMessageGroups ?? []
  const groupJsxCache = useMemo(
    () =>
      groups.map((group) => {
        const { id, name, phone_numbers, unread_indicator_count } = group
        const unreadCounts =
          unread_indicator_count > 99 ? '99+' : unread_indicator_count
        const pNums = ramdaFilter(propEq('is_primary_sms', true), phone_numbers)
        const unreadIndicator =
          unread_indicator_count > 0 ? (
            <UnreadWrapper>{unreadCounts}</UnreadWrapper>
          ) : undefined
        const label = (
          <MenuListItem
            primaryText={name}
            secondaryText={pNums[0]?.number_for_display}
            rightContent={unreadIndicator}
          />
        )
        return { value: id, content: label, selectedLabel: name }
      }),
    [userMessageGroups, selectedGroup],
  )

  const groupOptions = concat(
    [{ value: 0, content: t('messageThreads.allGroups') }],
    groupJsxCache,
  )
  const unselectedGroups = ramdaFilter((x) => x.id !== selectedGroup, groups)
  let unreads = 0
  unselectedGroups.forEach((group) => {
    unreads += group.unread_indicator_count
  })

  const tryGetThreadMessageGroup = (thread: MessageThread) => {
    return userMessageGroups?.find((mg) => mg.id === thread.message_group_id)
  }

  const handleSelectMessageThread = (thread: MessageThread) => {
    setSelectedThread(thread)
    onMessageThreadSelected(thread, tryGetThreadMessageGroup(thread))
  }

  const handleClickMessageThreadItem = (item: MessageThread) => () => {
    markMessageThreadRead(item)
    handleSelectMessageThread(item)
    item.unread_message_count = 0 //mark the thread read locally
    window.gtag('event', 'thread_selected', {
      id: item.id,
      group_id: item.message_group_id,
      number: item.external_phone_number,
    })
  }

  const handleSetUnreadsFilter = (e: number) => {
    setUnreadsFilter(e)
  }

  const handleSetSearchFilter = (searchValue: SearchValue) => {
    setSearchValue(searchValue)
  }

  const handleSelectGroup = (id: number) => {
    if (selectedGroup !== id) {
      setSelectedGroup(id)
    }
  }

  const handleGetNextThreadPage = () => {
    tryGetNextThreadPage()
  }

  const handleCreateNewChat = () => {
    window.gtag('event', 'new_thread_modal_opened', {})
    setMessagingModalState({ ...messagingModalState, open: true })
  }

  const handleOpenMarkReadModal = () => {
    setChatMarkAllReadModalState({
      open: true,
    })
  }

  function getTextingSubtitleText(selectedFilter) {
    switch (selectedFilter) {
      case ChatFilters.UNREAD:
        return t('messageThreads.emptyState.subtitle1')
      default:
        return t('messageThreads.emptyState.subtitle2')
    }
  }

  const handleThreadsUpdated = (updatedThreads: MessageThread[]) => {
    //replace existing threads with new data
    const carryoverThreads = visibleMessageThreads?.filter(
      (thread) => !updatedThreads.find((t) => t.id === thread.id),
    )
    const newSortedThreads = [
      ...(carryoverThreads ?? []),
      ...updatedThreads,
    ].sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
    setVisibleMessageThreads(newSortedThreads)
  }

  emitterSubscribe({
    subscriptionToken: 'message-thread-list-view:thread-created',
    subscription: {
      eventType: MessagingEmitterEventType.MESSAGE_THREAD_CREATED,
      callback: (tcPayload: ThreadCreatedPayload) => {
        setPendingAutoSelectedThreadId(tcPayload.thread.id)
      },
    },
  })

  //#endregion
  useDebounce(
    () => {
      if (searchValue.value === undefined && unreadsFilter === undefined) {
        return
      }

      //if filters change, reset the visible message threads, and set filters on the view model
      setFilters(
        searchValue.value,
        unreadsFilter === 1 ? true : undefined,
        selectedGroup,
        true,
      )
    },
    FILTER_CHANGE_DEBOUNCE_MS,
    [searchValue, unreadsFilter, selectedGroup],
  )

  //when searchFilter or unreadsFilter changes, immediately sets showLoader to true, and resets the visibleMessageThreads state while we wait for useDebounce to hit
  useEffect(() => {
    setVisibleMessageThreads([])
    setShowLoader(true)

    return () => {
      setFilters(undefined, undefined, undefined, false)
    }
  }, [searchValue, unreadsFilter, selectedGroup])

  //#region USE EFFECTS
  //on mount or when selectedGroup changes, get the latest message threads, set loading to true
  useEffect(() => {
    getMessageThreads
    setShowLoader(true)

    return () => {
      //on unmount, reset the selected thread - if this is not done, the model view could still technically be mounted somewhere, and automatically mark the thread as read
      setSelectedThread(undefined)
    }
  }, [])

  //watches for changes to messageThreads, updates the visibleMessageThreads state
  useEffect(() => {
    handleThreadsUpdated(
      messageThreads.messageThreadsByGroup.get(selectedGroup) ?? [],
    )
  }, [messageThreads])

  //handles the loading state of the list
  useEffect(() => {
    if (firstLoadStarted && !messageThreadsLoading) {
      setShowLoader(false)
    }

    if (!firstLoadStarted && messageThreadsLoading) {
      setFirstLoadStarted(true)
    }

    if (
      messageThreadsLoading &&
      visibleMessageThreads &&
      visibleMessageThreads.length > 0
    ) {
      setShowLoader(true)
    }
  }, [messageThreadsLoading])

  //selectedThread may change within the view model after a query, when it does, inform the parent
  useEffect(() => {
    if (selectedThread) {
      onMessageThreadSelected(
        selectedThread,
        tryGetThreadMessageGroup(selectedThread),
      )
    }
  }, [selectedThread])

  //watches for pending thread selection, checks to see if the pending thread is in the list of visible threads, selects the thread, then unsets the pending thread
  useEffect(() => {
    if (!pendingAutoSelectedThreadId) {
      return
    }

    const thread = visibleMessageThreads?.find(
      (t) => t.id === pendingAutoSelectedThreadId,
    )
    if (!thread) {
      return
    }

    handleSelectMessageThread(thread)
    setPendingAutoSelectedThreadId(undefined)
  }, [pendingAutoSelectedThreadId, visibleMessageThreads])
  //#endregion

  return (
    <VStack
      display="flex"
      height="100%"
      alignSelf="stretch"
      spacing="2px"
      bg="gray.300"
    >
      <Flex
        width="100%"
        padding="1rem"
        justifyContent="center"
        alignItems="center"
        gap="0.75rem"
        alignSelf="stretch"
        backgroundColor="gray.200"
      >
        <ToggleListButton hidden clickBehavior="always-close" />
        <SearchInput
          placeholder={t('messageThreads.searchPlaceholder')}
          height="40px"
          onChange={handleSetSearchFilter}
          value={searchValue.label}
        />
      </Flex>
      <ItemWrapper>
        <DropdownSelect
          items={groupOptions}
          onSelect={handleSelectGroup}
          selectedValue={selectedGroup}
        />
      </ItemWrapper>
      {unreads > 0 && selectedGroup !== 0 && (
        <NotificationWrapper key={unreads} onClick={() => handleSelectGroup(0)}>
          <DotNotifier />
          <Text
            fontStyle={FontStyle.Tiny}
            text={t('messageThreads.new', { count: unreads })}
            color={light.primary}
          />
        </NotificationWrapper>
      )}
      <StyledItemWrapper
        style={{
          paddingBottom: 0,
        }}
      >
        <FilterWrapper>
          <FilterMenu
            selectedFilter={unreadsFilter}
            options={ChatOptions()}
            onChange={(e) => handleSetUnreadsFilter(e as number)}
          />
        </FilterWrapper>
        <QuickActionButton
          actionIcon={markAsRead}
          tooltipText={t('messageThreads.markAllRead')}
          onClick={handleOpenMarkReadModal}
          variant={QuickActionButtonVariants.Default_200}
        />
      </StyledItemWrapper>
      {threads && threads.length > 0 ? (
        <ScrollingList
          display="flex"
          flex="1"
          width="100%"
          height="70%"
          backgroundColor="gray.200"
          px=".25rem"
          py=".75rem"
          scrollbarOptions={{ showOnHoverOnly: true }}
          itemData={threads}
          onEndReached={handleGetNextThreadPage}
          showLoaderFooter={messageThreadsLoading}
          onBuildItemContent={(_, thread: MessageThread) => {
            const { id, updated_at, latest_message, unread_message_count } =
              thread

            const unRead = unread_message_count > 0
            const textPlaceholder =
              (latest_message?.direction ?? '') === 'inbound'
                ? t('messagingPage.textPlaceholder.receivedFile')
                : t('messagingPage.textPlaceholder.fileSent')

            const activated = equals(selectedThread?.id, id)

            const threadName = thread.name
            const chunks = findAll({
              autoEscape: true,
              searchWords: [searchValue.value ?? ''],
              textToHighlight: threadName,
            })

            const titleElement = (
              <NameWrapper>
                {searchValue.value !== '' ? (
                  <TitleWrapper>
                    {chunks.map((chunk, index) => {
                      const { end, highlight, start } = chunk
                      const text = threadName.substr(start, end - start)
                      return (
                        <StyledTitleText
                          key={`key-${index}`}
                          fontStyle={FontStyle.Normal}
                          text={text}
                          color={highlight ? light.dark900 : light.dark700}
                          weight={highlight ? '700' : 'normal'}
                        />
                      )
                    })}
                  </TitleWrapper>
                ) : (
                  <StyledTitleText
                    fontStyle={FontStyle.Normal}
                    text={threadName}
                    color={light.dark900}
                    weight={unRead ? '700' : 'normal'}
                  />
                )}
              </NameWrapper>
            )
            const subTitle = (
              <StyledSubtitleText
                fontStyle={FontStyle.Tiny}
                text={
                  !latest_message?.body && Array.isArray(latest_message?.files)
                    ? textPlaceholder
                    : latest_message?.body
                }
                color={
                  activated
                    ? light.dark800
                    : unRead
                      ? light.dark800
                      : light.dark700
                }
              />
            )
            return (
              <ListItem
                key={`text-item-${id}`}
                activated={activated}
                title={titleElement}
                subTitle={subTitle}
                shouldShowUnreadDot={unRead}
                date={
                  <Text
                    fontStyle={FontStyle.Tiny}
                    text={formatDateString(updated_at)}
                    color={light.dark800}
                  />
                }
                onClick={handleClickMessageThreadItem(thread)}
              />
            )
          }}
        />
      ) : showLoader ? (
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      ) : (
        <ListWrapper>
          {hasValue(searchValue.label) ? (
            <EmptySearch search={searchValue.value} />
          ) : (
            <EmptyState
              title={t('messageThreads.emptyState.title')}
              subtitle={`${getTextingSubtitleText(unreadsFilter)}`}
              buttonText={t('messageThreads.buttonText')}
              onClick={handleCreateNewChat}
            />
          )}
        </ListWrapper>
      )}
    </VStack>
  )
}

export default MessageThreadsListView
